import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Box } from '@mui/material';
import { MARKETPLACE_CATEGORY_ALL } from '../../utils/marketplace/marketplaceKeywordSearch';
import {
  HEADER_FACET_CATEGORY_COLUMN_LIMIT,
  HEADER_MARKETPLACE_FACET_LABELS,
  getHeaderCategoryColumns,
  getHeaderFacetSelectionState,
  getHeaderFormatItems,
  getHeaderPlatformItems,
  getVisibleHeaderFacetChips,
  isHeaderFacetChipActive,
} from '../../utils/marketplace/headerMarketplaceFacetItems';
import { MARKETPLACE_ART } from '../marketplace/shared/marketplaceArtAssets';
import { FONT_SIZES } from '../../Theme';
import HeaderFacetLinkList, { headerFacetLinkButtonSx } from './headerFacetLinkList';

const OPEN_HOVER_DELAY_MS = 250;
const LEAVE_CLOSE_DELAY_MS = 150;

const MENU_FACET_IDS = new Set(['platforms', 'categories', 'format']);

const PROMO_COPY = Object.freeze({
  platforms: {
    title: 'Platforms',
    cta: 'Browse now',
  },
  categories: {
    title: 'Categories',
    cta: 'Browse now',
  },
  format: {
    title: 'Format',
    cta: 'Browse now',
  },
});

const FACET_PROMO_ART = Object.freeze({
  platforms: MARKETPLACE_ART.facetPromoPlatforms,
  categories: MARKETPLACE_ART.facetPromoCategories,
  format: MARKETPLACE_ART.facetPromoFormat,
});

/**
 * Mega-menu body height: eBay RTM banner (270) + vertical padding so the promo
 * never sits flush against the menu chrome.
 */
const FACET_MENU_BODY_HEIGHT = { xs: 268, md: 310 };

const linkButtonSx = {
  ...headerFacetLinkButtonSx,
  whiteSpace: 'nowrap',
};

const columnHeadingSx = {
  appearance: 'none',
  border: 0,
  background: 'transparent',
  m: 0,
  p: 0,
  font: 'inherit',
  fontSize: FONT_SIZES.bodySm,
  fontWeight: 700,
  lineHeight: 1.25,
  color: 'text.primary',
  textAlign: 'left',
  whiteSpace: 'nowrap',
};

const columnHeadingRuleSx = {
  flexShrink: 0,
  width: '100%',
  height: 0,
  mt: 1,
  mb: 1,
  border: 0,
  borderBottom: '1px solid',
  borderColor: 'grey.300',
};

function FacetLinkColumn({
  heading,
  items,
  onSelect,
  onHeadingClick,
  testIdPrefix,
  allowScroll = true,
}) {
  return (
    <Box
      component="section"
      aria-label={heading}
      sx={{
        flex: '0 0 auto',
        alignSelf: 'stretch',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'stretch',
        minWidth: 'max-content',
        minHeight: 0,
      }}
    >
      {onHeadingClick ? (
        <Box
          component="button"
          type="button"
          data-testid={`${testIdPrefix}-heading`}
          onClick={onHeadingClick}
          sx={{
            ...columnHeadingSx,
            cursor: 'pointer',
            '&:hover': {
              color: 'primary.main',
              textDecoration: 'underline',
            },
            '&:focus-visible': {
              outline: (theme) => `2px solid ${theme.palette.primary.main}`,
              outlineOffset: 2,
            },
          }}
        >
          {heading}
        </Box>
      ) : (
        <Box
          component="h3"
          sx={columnHeadingSx}
        >
          {heading}
        </Box>
      )}
      <Box aria-hidden sx={columnHeadingRuleSx} />
      <HeaderFacetLinkList
        items={items}
        onSelect={onSelect}
        testIdPrefix={testIdPrefix}
        buttonSx={linkButtonSx}
        listSx={{
          gap: 0.4,
          flex: 1,
          minHeight: 0,
          overflowY: allowScroll ? 'auto' : 'hidden',
          scrollbarWidth: allowScroll ? 'thin' : 'none',
        }}
      />
    </Box>
  );
}

/** Authored banner pixel size — WebP assets are 1540×540 (2x). */
const FACET_PROMO_WIDTH = 770;
const FACET_PROMO_HEIGHT = 270;
/** Display cap so the art stays a card, not a full-bleed strip. */
const FACET_PROMO_MAX_WIDTH = 720;

function FacetPromoCard({
  facetId,
  title,
  ctaLabel,
  onBrowse,
  promoImageSrc,
}) {
  return (
    <Box
      component="aside"
      aria-label={`${title} highlight`}
      sx={{
        position: 'relative',
        minWidth: 0,
        width: '100%',
        maxWidth: FACET_PROMO_MAX_WIDTH,
        height: '100%',
        alignSelf: 'stretch',
        justifySelf: 'end',
        ml: 'auto',
        borderRadius: 2,
        bgcolor: 'action.hover',
        overflow: 'hidden',
        display: 'flex',
        alignItems: 'stretch',
      }}
    >
      {promoImageSrc ? (
        <Box
          component="img"
          src={promoImageSrc}
          alt=""
          width={FACET_PROMO_WIDTH}
          height={FACET_PROMO_HEIGHT}
          sx={{
            position: 'absolute',
            inset: 0,
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            objectPosition: 'right center',
          }}
        />
      ) : null}

      <Box
        sx={{
          position: 'relative',
          zIndex: 1,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'flex-start',
          gap: 1.25,
          px: { xs: 2.25, md: 3 },
          py: 2,
          width: { xs: '42%', md: '38%' },
          maxWidth: 240,
        }}
      >
        <Box
          component="p"
          sx={{
            m: 0,
            fontSize: FONT_SIZES.title,
            fontWeight: 700,
            lineHeight: 1.15,
            color: 'text.primary',
          }}
        >
          {title}
        </Box>
        <Box
          component="button"
          type="button"
          data-testid={`header-facet-promo-cta-${facetId}`}
          onClick={onBrowse}
          sx={{
            appearance: 'none',
            border: 0,
            m: 0,
            px: 1.6,
            py: 0.75,
            borderRadius: 999,
            bgcolor: 'primary.main',
            color: 'primary.contrastText',
            font: 'inherit',
            fontSize: FONT_SIZES.caption,
            fontWeight: 600,
            cursor: 'pointer',
            transition: 'background-color 140ms ease',
            '&:hover': {
              bgcolor: 'primary.dark',
            },
            '&:focus-visible': {
              outline: (theme) => `2px solid ${theme.palette.primary.main}`,
              outlineOffset: 2,
            },
          }}
        >
          {`${ctaLabel} →`}
        </Box>
      </Box>
    </Box>
  );
}

/**
 * eBay-style facet row for site chrome search.
 * Platforms / Categories / Format open hover mega menus; All clears facets.
 */
function HeaderMarketplaceCategoryFilters({
  category = MARKETPLACE_CATEGORY_ALL,
  platforms = [],
  contentTypes = [],
  saved = false,
  followingBrands = false,
  onSelectCategory = () => {},
  onSelectPlatform = () => {},
  onSelectContentFormat = () => {},
  onSelectSaved = () => {},
  onSelectBrandsYouFollow = () => {},
  onClearFacets = () => {},
  userType = null,
  dense = false,
}) {
  const [openFacet, setOpenFacet] = useState(null);
  const openTimerRef = useRef(null);
  const closeTimerRef = useRef(null);
  const rootRef = useRef(null);

  const clearOpenTimer = useCallback(() => {
    if (openTimerRef.current != null) {
      window.clearTimeout(openTimerRef.current);
      openTimerRef.current = null;
    }
  }, []);

  const clearCloseTimer = useCallback(() => {
    if (closeTimerRef.current != null) {
      window.clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  }, []);

  const openMenu = useCallback((facetId) => {
    clearOpenTimer();
    clearCloseTimer();
    setOpenFacet(facetId);
  }, [clearCloseTimer, clearOpenTimer]);

  const scheduleOpen = useCallback((facetId) => {
    clearCloseTimer();
    // Once a mega menu is open, switching chips should feel instant.
    if (openFacet != null) {
      clearOpenTimer();
      setOpenFacet(facetId);
      return;
    }
    clearOpenTimer();
    openTimerRef.current = window.setTimeout(() => {
      setOpenFacet(facetId);
      openTimerRef.current = null;
    }, OPEN_HOVER_DELAY_MS);
  }, [clearCloseTimer, clearOpenTimer, openFacet]);

  const scheduleClose = useCallback(() => {
    clearOpenTimer();
    clearCloseTimer();
    closeTimerRef.current = window.setTimeout(() => {
      setOpenFacet(null);
      closeTimerRef.current = null;
    }, LEAVE_CLOSE_DELAY_MS);
  }, [clearCloseTimer, clearOpenTimer]);

  const closeMenu = useCallback(() => {
    clearOpenTimer();
    clearCloseTimer();
    setOpenFacet(null);
  }, [clearCloseTimer, clearOpenTimer]);

  useEffect(() => () => {
    clearOpenTimer();
    clearCloseTimer();
  }, [clearCloseTimer, clearOpenTimer]);

  useEffect(() => {
    if (!openFacet) return undefined;

    const onKeyDown = (event) => {
      if (event.key === 'Escape') {
        closeMenu();
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [closeMenu, openFacet]);

  const facetSelection = getHeaderFacetSelectionState({
    category,
    platforms,
    contentTypes,
    saved,
    followingBrands,
    userType,
  });
  const { allSelected } = facetSelection;

  const handleBrowseDimension = (facetId) => {
    if (facetId === 'platforms') {
      onSelectPlatform('');
    } else if (facetId === 'categories') {
      onSelectCategory(MARKETPLACE_CATEGORY_ALL);
    } else if (facetId === 'format') {
      onSelectContentFormat('');
    }
    closeMenu();
  };

  const handleChipClick = (facetId) => {
    if (facetId === 'all') {
      closeMenu();
      onClearFacets();
      return;
    }
    if (facetId === 'saved') {
      closeMenu();
      onSelectSaved();
      return;
    }
    if (facetId === 'brandsYouFollow') {
      closeMenu();
      onSelectBrandsYouFollow();
      return;
    }
    // Chip click browses that whole dimension (all platforms / categories / formats).
    // Hover / focus still opens the mega menu for picking a specific option.
    handleBrowseDimension(facetId);
  };

  const visibleFacetChips = getVisibleHeaderFacetChips(userType);

  const handleSelectPlatform = (value) => {
    onSelectPlatform(value);
    closeMenu();
  };

  const handleSelectCategory = (value) => {
    onSelectCategory(value || MARKETPLACE_CATEGORY_ALL);
    closeMenu();
  };

  const handleSelectFormat = (value) => {
    onSelectContentFormat(value);
    closeMenu();
  };

  const categoryColumns = getHeaderCategoryColumns(userType, {
    columnLimit: HEADER_FACET_CATEGORY_COLUMN_LIMIT,
  });
  const platformItems = getHeaderPlatformItems();
  const formatItems = getHeaderFormatItems();

  const promoImageSrc = openFacet ? FACET_PROMO_ART[openFacet] || null : null;
  const promo = openFacet ? PROMO_COPY[openFacet] : null;
  let menuGridColumns = `max-content minmax(0, 1fr)`;
  if (openFacet === 'categories') {
    menuGridColumns = `max-content max-content minmax(0, 1fr)`;
  }

  return (
    <Box
      ref={rootRef}
      component="nav"
      aria-label="Marketplace facet filters"
      data-testid="header-marketplace-category-filters"
      onMouseLeave={scheduleClose}
      sx={{
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        width: '100%',
        height: '100%',
        minWidth: 0,
        mt: 0,
      }}
    >
      <Box
        component="ul"
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-start',
          width: '100%',
          height: '100%',
          gap: dense ? 1.25 : 2,
          m: 0,
          p: 0,
          listStyle: 'none',
          overflowX: 'auto',
          overflowY: 'hidden',
          WebkitOverflowScrolling: 'touch',
          scrollbarWidth: 'none',
          '&::-webkit-scrollbar': {
            display: 'none',
          },
        }}
      >
        {visibleFacetChips.map((facetId, index) => {
          const isOpen = openFacet === facetId;
          const isActive = isHeaderFacetChipActive(facetId, facetSelection, { isOpen });

          const hasPopup = MENU_FACET_IDS.has(facetId);
          let itemPaddingX = dense ? 0.25 : 0.5;
          if (index === 0) itemPaddingX = 0;

          return (
            <Box
              component="li"
              key={facetId}
              sx={{ flexShrink: 0 }}
            >
              <Box
                component="button"
                type="button"
                data-testid={`header-marketplace-facet-${facetId}`}
                aria-expanded={hasPopup ? isOpen : undefined}
                aria-haspopup={hasPopup ? 'true' : undefined}
                aria-pressed={facetId === 'all' ? allSelected : isActive}
                onClick={() => handleChipClick(facetId)}
                onMouseEnter={() => {
                  if (hasPopup) {
                    scheduleOpen(facetId);
                  } else {
                    clearOpenTimer();
                  }
                }}
                onFocus={() => {
                  if (hasPopup) openMenu(facetId);
                }}
                sx={{
                  appearance: 'none',
                  border: 0,
                  background: 'transparent',
                  m: 0,
                  pl: itemPaddingX,
                  pr: dense ? 0.25 : 0.5,
                  py: dense ? 0.35 : 0.5,
                  font: 'inherit',
                  fontSize: FONT_SIZES.bodySm,
                  fontWeight: isActive ? 600 : 500,
                  lineHeight: 1.2,
                  color: isActive ? 'text.primary' : 'text.secondary',
                  cursor: 'pointer',
                  whiteSpace: 'nowrap',
                  borderRadius: 0,
                  boxShadow: isOpen
                    ? (theme) => `inset 0 -2px 0 ${theme.palette.primary.main}`
                    : 'none',
                  transition: 'color 140ms ease, border-color 140ms ease',
                  '&:hover': {
                    color: 'text.primary',
                  },
                  '&:focus-visible': {
                    outline: (theme) => `2px solid ${theme.palette.primary.main}`,
                    outlineOffset: 2,
                  },
                }}
              >
                {HEADER_MARKETPLACE_FACET_LABELS[facetId]}
              </Box>
            </Box>
          );
        })}
      </Box>

      {openFacet && promo ? (
        <Box
          role="region"
          aria-label={`${HEADER_MARKETPLACE_FACET_LABELS[openFacet]} menu`}
          data-testid={`header-facet-menu-${openFacet}`}
          onMouseEnter={clearCloseTimer}
          sx={{
            position: 'absolute',
            top: 'calc(100% + 4px)',
            left: 0,
            right: 0,
            width: '100%',
            maxWidth: '100%',
            zIndex: 20,
            height: FACET_MENU_BODY_HEIGHT,
            bgcolor: 'background.paper',
            borderRadius: 2,
            boxShadow: (theme) => theme.palette.chat?.windowShadow || theme.shadows[8],
            border: (theme) => `1px solid ${theme.palette.border?.subtle || theme.palette.divider}`,
            px: { xs: 1.75, md: 2.25 },
            py: { xs: 1.75, md: 2 },
            display: 'grid',
            gridTemplateColumns: menuGridColumns,
            justifyContent: 'space-between',
            gap: { xs: 2, md: 2.5 },
            alignItems: 'stretch',
            overflow: 'hidden',
            boxSizing: 'border-box',
          }}
        >
          {openFacet === 'platforms' ? (
            <FacetLinkColumn
              heading="Platforms"
              items={platformItems}
              onSelect={handleSelectPlatform}
              onHeadingClick={() => handleBrowseDimension('platforms')}
              testIdPrefix="header-facet-platform"
            />
          ) : null}

          {openFacet === 'categories' ? (
            <>
              <FacetLinkColumn
                heading="Top categories"
                items={categoryColumns.top}
                onSelect={handleSelectCategory}
                onHeadingClick={() => handleBrowseDimension('categories')}
                testIdPrefix="header-facet-category-top"
                allowScroll={false}
              />
              <FacetLinkColumn
                heading="More categories"
                items={categoryColumns.more}
                onSelect={handleSelectCategory}
                onHeadingClick={() => handleBrowseDimension('categories')}
                testIdPrefix="header-facet-category-more"
                allowScroll={false}
              />
            </>
          ) : null}

          {openFacet === 'format' ? (
            <FacetLinkColumn
              heading="Format"
              items={formatItems}
              onSelect={handleSelectFormat}
              onHeadingClick={() => handleBrowseDimension('format')}
              testIdPrefix="header-facet-format"
            />
          ) : null}

          <FacetPromoCard
            facetId={openFacet}
            title={promo.title}
            ctaLabel={promo.cta}
            onBrowse={() => handleBrowseDimension(openFacet)}
            promoImageSrc={promoImageSrc}
          />
        </Box>
      ) : null}
    </Box>
  );
}

export default HeaderMarketplaceCategoryFilters;
